/**
 * ZUsers - Zod Validation Schemas for the Users API (/api/users/*)
 *
 * Controller: users/users.controller.ts
 *
 * The legacy `users.service.ts` is a large, heterogeneous surface (UI prefs,
 * credentials, macros, tools, billing, API keys, personal tokens, webhooks,
 * user groups). It is being migrated onto NexusApi in cohesive SUB-SLICES that
 * each extend this manifest — no re-registration is needed once the domain
 * exists. Covered so far: message macros, UI preferences, credentials.
 */

import { z } from "zod";

import type { DbEntity } from "../../../client";
import { TrackingSchema } from "../../../schemas/User/tracking/__json.schema";
import {
  createOrUpdateCredentialSchema,
  uiPreferencesSchema
} from "../../../shared/domain/users/users.types";
import { ApiSuccessSchema } from "../../common/response.schema";
import type { ScopeGroup } from "../../public/v1/scopes";

/**
 * Response shape of GET /api/users/me/settings — the full current-user row
 * plus the active organization, the org's current subscription, and a
 * trial-availability flag. `organization` and `subscription` are nullable
 * because the underlying `findUnique`/`findFirst` calls can return null.
 * Modelled with `z.custom` passthrough: this is a heterogeneous full-entity
 * projection (DbEntity.User with relations), not a stable field-by-field
 * contract, so runtime validation stays a no-op and preserves the exact
 * legacy payload.
 */
export type UserSettingsResponse = DbEntity.User & {
  organization: DbEntity.Organization | null;
  subscription: DbEntity.Subscription | null;
  isTrialAvailable: boolean;
};

// ═══════════════════════════════════════════════════════════════
// MACROS
// ═══════════════════════════════════════════════════════════════

const MacroPathVarsSchema = z.object({ macroId: z.string() });

/**
 * A macro's writable fields. The service spreads the body straight into Prisma,
 * so this closed schema is also the mass-assignment guard — only name/content
 * reach the database, never id/organizationId/timestamps.
 */
const CreateMacroBodySchema = z.object({
  name: z.string().trim().min(1),
  content: z.string().min(1)
});

const UpdateMacroBodySchema = z.object({
  name: z.string().trim().min(1).optional(),
  content: z.string().min(1).optional()
});

const MacroOkSchema = z.object({ success: z.literal(true) });

// ═══════════════════════════════════════════════════════════════
// UI PREFERENCES
// ═══════════════════════════════════════════════════════════════

/**
 * Update body is a partial of the full preferences shape — the server merges it
 * with existing preferences (only present fields are touched). Mirrors the
 * retired UpdateUiPreferencesDto (createZodDto(uiPreferencesSchema.partial())).
 */
const UpdateUiPreferencesBodySchema = uiPreferencesSchema.partial();

// ═══════════════════════════════════════════════════════════════
// CREDENTIALS
// ═══════════════════════════════════════════════════════════════

/** Body for the OpenAI key smoke-test endpoint. */
const TestOpenAiKeyBodySchema = z.object({ apiKey: z.string() });

// ═══════════════════════════════════════════════════════════════
// USER GROUPS (users/services/permissions/permissions.controller.ts)
// ═══════════════════════════════════════════════════════════════

const UserGroupPathVarsSchema = z.object({ userGroupId: z.string() });

/**
 * Create/update body. The service destructures only `name` and `userIds` (no
 * body spread into Prisma), so this closed schema doubles as the mass-assignment
 * guard. Empty/pending-invite userIds are filtered server-side, but a group must
 * carry a name — every UI path already disables submit on a blank name.
 */
const UserGroupWriteBodySchema = z.object({
  name: z.string().trim().min(1),
  userIds: z.array(z.string()).optional()
});

const AddUserToGroupBodySchema = z.object({ userIdToAdd: z.string().min(1) });
const RemoveUserFromGroupBodySchema = z.object({ userIdToRemove: z.string().min(1) });

const UserGroupOkSchema = z.object({ success: z.literal(true) });

/**
 * The list endpoint returns each group with a slim membership projection
 * (user ids only) — the FE derives member counts and assignment state from it.
 */
type UserGroupWithUserIds = DbEntity.UserGroup & { users: { id: string }[] };

/**
 * Every credential response masks its key (last 4 chars only), so the wire `key`
 * is a masked string or null — never the raw secret. Every other field is the
 * plain UserCredential row (Date fields serialize to ISO strings over HTTP).
 *
 * Shared by the list read and the upsert write, because both already mask at
 * runtime. Naming that in the contract is what keeps it true: `DbEntity.UserCredential`
 * types `key` as the stored column, so a response declared with it reads as a
 * promise to hand back the secret, and the next reader has no way to tell that
 * the service masks it. There is deliberately no contract in this file whose
 * `key` means the plaintext value.
 */
type MaskedUserCredential = Omit<DbEntity.UserCredential, "key"> & { key: string | null };

// ═══════════════════════════════════════════════════════════════
// API KEYS + PERSONAL TOKENS (users/users.controller.ts)
// ═══════════════════════════════════════════════════════════════

const ApiKeyPathVarsSchema = z.object({ keyId: z.string() });

/**
 * Create body. `scopes` is not min-constrained: the empty array is a valid
 * legacy state ("No scopes"), and the service validates scope strings itself.
 * `expiresAt` is an ISO string the service parses to a Date.
 */
const CreateApiKeyBodySchema = z.object({
  label: z.string().optional(),
  scopes: z.array(z.string()),
  expiresAt: z.string().optional()
});

/**
 * Update body. `expiresAt` is tri-state: an ISO string sets it, `null` clears an
 * existing expiration, and absence leaves it untouched.
 */
const UpdateApiKeyBodySchema = z.object({
  label: z.string().optional(),
  scopes: z.array(z.string()).optional(),
  expiresAt: z.string().nullable().optional()
});

/** The raw secret is returned ONCE, only at creation. */
const CreatedKeySchema = z.object({ key: z.string() });
const ApiKeyOkSchema = z.object({ success: z.literal(true) });

/**
 * Masked api-key / personal-token wire item. The raw secret columns (`key`,
 * `keyHash`, `encryptedKey`) NEVER cross the wire — `key` here is the masked
 * keyHint (e.g. "nxs_u_...a3f4"). Date fields serialize to ISO strings over HTTP.
 * Personal tokens are org-less, so they carry no `role`.
 */
type MaskedApiKey = {
  id: string;
  key: string;
  label: string | null;
  role: string | null;
  scopes: string[];
  expiresAt: Date | null;
  lastUsedAt: Date | null;
  createdAt: Date;
};
type MaskedPersonalToken = Omit<MaskedApiKey, "role">;

// ═══════════════════════════════════════════════════════════════
// BILLING / STRIPE (users/users.controller.ts → UsersBillingService)
// ═══════════════════════════════════════════════════════════════

/**
 * Checkout params travel on the query string (the controller reads them via
 * `@Query`), so they are declared as `Params`, not a body. `type` selects a
 * subscription tier or a one-off top-up; `clientReferenceId` is a Rewardful
 * referral tag; `from` is an optional origin hint.
 */
const CheckoutSessionParamsSchema = z.object({
  type: z.enum(["SUBSCRIPTION_HOBBY", "SUBSCRIPTION_PRO", "TOPUP"]),
  clientReferenceId: z.string().optional(),
  from: z.string().optional()
});

/**
 * Top-up params (query string). `amount` arrives as a string over HTTP and is
 * coerced to a number — matching the legacy handler, which fed the raw query
 * value straight into `Math.floor`.
 */
const TopupParamsSchema = z.object({
  amount: z.coerce.number(),
  clientReferenceId: z.string().optional()
});

/** Enable/disable automatic balance top-up. */
const ToggleAutomaticTopupBodySchema = z.object({ toggle: z.boolean() });

/**
 * Stripe checkout session id — the FE hands it to `stripe.redirectToCheckout`.
 * `z.custom` (no runtime validation) preserves the legacy service's error path,
 * which returns the caught Stripe error object (serialized to `{}`) rather than
 * throwing, so this envelope migration changes no behaviour.
 */
const CheckoutSessionSchema = z.custom<{ id: string }>();

/**
 * Top-up outcome. `paid` reflects whether the immediate invoice charge
 * succeeded. `id` is only populated on the checkout-fallback path (unreachable
 * from this controller, which never sets `createCheckoutSession`) — kept
 * optional so the caller compiles against the legacy shape.
 */
const TopupResultSchema = z.object({ paid: z.boolean(), id: z.string().optional() });

/** Toggle simply persists the flag and echoes success. */
const ToggleTopupResultSchema = z.object({ success: z.literal(true) });

/**
 * The subscription mutations (upgrade / downgrade / (un)subscribe / trial)
 * return a raw boolean `true` on success. The legacy service also returns the
 * caught error object on its failure path (serialized to `{}` over the wire);
 * `z.custom` keeps that passthrough intact — no client-side parse throw.
 */
const SubscriptionMutationResultSchema = z.custom<boolean>();

// ═══════════════════════════════════════════════════════════════
// ZUSERS MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZUsers = {
  /** GET /api/users/me/macros — all macros for the org. */
  GetMacros: {
    method: "GET" as const,
    path: "/api/users/me/macros",
    Response: ApiSuccessSchema(z.array(z.custom<DbEntity.Macro>()))
  },

  /** POST /api/users/me/macros — create a macro. */
  CreateMacro: {
    method: "POST" as const,
    path: "/api/users/me/macros",
    Body: CreateMacroBodySchema,
    Response: ApiSuccessSchema(z.custom<DbEntity.Macro>())
  },

  /**
   * PUT /api/users/me/macros/:macroId — update a macro.
   * The org-scoped update is a batch operation (updateMany), so it acknowledges
   * success rather than echoing the row — consumers refetch the list.
   */
  UpdateMacro: {
    method: "PUT" as const,
    path: "/api/users/me/macros/:macroId",
    PathVars: MacroPathVarsSchema,
    Body: UpdateMacroBodySchema,
    Response: ApiSuccessSchema(MacroOkSchema)
  },

  /** DELETE /api/users/me/macros/:macroId — delete a macro. */
  DeleteMacro: {
    method: "DELETE" as const,
    path: "/api/users/me/macros/:macroId",
    PathVars: MacroPathVarsSchema,
    Response: ApiSuccessSchema(MacroOkSchema)
  },

  /** GET /api/users/me/ui-preferences — current user's UI preferences (merged with defaults). */
  GetUiPreferences: {
    method: "GET" as const,
    path: "/api/users/me/ui-preferences",
    Response: ApiSuccessSchema(uiPreferencesSchema)
  },

  /** PATCH /api/users/me/ui-preferences — partial update, returns the merged result. */
  UpdateUiPreferences: {
    method: "PATCH" as const,
    path: "/api/users/me/ui-preferences",
    Body: UpdateUiPreferencesBodySchema,
    Response: ApiSuccessSchema(uiPreferencesSchema)
  },

  /** GET /api/users/me/credentials — all third-party credentials for the org, keys masked. */
  GetCredentials: {
    method: "GET" as const,
    path: "/api/users/me/credentials",
    Response: ApiSuccessSchema(z.array(z.custom<MaskedUserCredential>()))
  },

  /** POST /api/users/me/credentials — create or update (upsert) a provider credential, key masked. */
  UpsertCredential: {
    method: "POST" as const,
    path: "/api/users/me/credentials",
    Body: createOrUpdateCredentialSchema,
    Response: ApiSuccessSchema(z.custom<MaskedUserCredential>())
  },

  /** POST /api/users/me/credentials/openai/test — validate an OpenAI key (throws 400 if invalid). */
  TestOpenAiKey: {
    method: "POST" as const,
    path: "/api/users/me/credentials/openai/test",
    Body: TestOpenAiKeyBodySchema,
    Response: ApiSuccessSchema(z.object({ valid: z.literal(true) }))
  },

  /** GET /api/users/permissions/userGroups — all groups in the org (admin only), members as ids. */
  GetUserGroups: {
    method: "GET" as const,
    path: "/api/users/permissions/userGroups",
    Response: ApiSuccessSchema(z.array(z.custom<UserGroupWithUserIds>()))
  },

  /** POST /api/users/permissions/userGroups — create a group, optionally seeding members. */
  CreateUserGroup: {
    method: "POST" as const,
    path: "/api/users/permissions/userGroups",
    Body: UserGroupWriteBodySchema,
    Response: ApiSuccessSchema(z.custom<DbEntity.UserGroup>())
  },

  /** PUT /api/users/permissions/userGroups/:userGroupId — rename and/or replace the member set. */
  UpdateUserGroup: {
    method: "PUT" as const,
    path: "/api/users/permissions/userGroups/:userGroupId",
    PathVars: UserGroupPathVarsSchema,
    Body: UserGroupWriteBodySchema,
    Response: ApiSuccessSchema(z.custom<DbEntity.UserGroup>())
  },

  /**
   * DELETE /api/users/permissions/userGroups/:userGroupId — hard-delete a group.
   * Acknowledges success rather than echoing the deleted row (consumers refetch).
   */
  DeleteUserGroup: {
    method: "DELETE" as const,
    path: "/api/users/permissions/userGroups/:userGroupId",
    PathVars: UserGroupPathVarsSchema,
    Response: ApiSuccessSchema(UserGroupOkSchema)
  },

  /** POST /api/users/permissions/userGroups/:userGroupId/addUser — add one member. */
  AddUserToUserGroup: {
    method: "POST" as const,
    path: "/api/users/permissions/userGroups/:userGroupId/addUser",
    PathVars: UserGroupPathVarsSchema,
    Body: AddUserToGroupBodySchema,
    Response: ApiSuccessSchema(z.custom<DbEntity.UserGroup>())
  },

  /** POST /api/users/permissions/userGroups/:userGroupId/removeUser — remove one member. */
  RemoveUserFromUserGroup: {
    method: "POST" as const,
    path: "/api/users/permissions/userGroups/:userGroupId/removeUser",
    PathVars: UserGroupPathVarsSchema,
    Body: RemoveUserFromGroupBodySchema,
    Response: ApiSuccessSchema(z.custom<DbEntity.UserGroup>())
  },

  /** POST /api/users/me/api-key/user — create a per-user scoped key; returns the raw key once. */
  CreateUserApiKey: {
    method: "POST" as const,
    path: "/api/users/me/api-key/user",
    Body: CreateApiKeyBodySchema,
    Response: ApiSuccessSchema(CreatedKeySchema)
  },

  /** GET /api/users/me/api-key/user — list the user's active keys (secret masked). */
  ListUserApiKeys: {
    method: "GET" as const,
    path: "/api/users/me/api-key/user",
    Response: ApiSuccessSchema(z.array(z.custom<MaskedApiKey>()))
  },

  /** PATCH /api/users/me/api-key/user/:keyId — update label/scopes/expiry (secret masked). */
  UpdateUserApiKey: {
    method: "PATCH" as const,
    path: "/api/users/me/api-key/user/:keyId",
    PathVars: ApiKeyPathVarsSchema,
    Body: UpdateApiKeyBodySchema,
    Response: ApiSuccessSchema(z.custom<MaskedApiKey>())
  },

  /** DELETE /api/users/me/api-key/user/:keyId — revoke a key. */
  RevokeUserApiKey: {
    method: "DELETE" as const,
    path: "/api/users/me/api-key/user/:keyId",
    PathVars: ApiKeyPathVarsSchema,
    Response: ApiSuccessSchema(ApiKeyOkSchema)
  },

  /** GET /api/users/me/api-key/scopes — the static scope taxonomy for the scope picker. */
  GetApiKeyScopes: {
    method: "GET" as const,
    path: "/api/users/me/api-key/scopes",
    Response: ApiSuccessSchema(z.array(z.custom<ScopeGroup>()))
  },

  /** POST /api/users/me/personal-token — create a cross-org token; returns the raw key once. */
  CreatePersonalToken: {
    method: "POST" as const,
    path: "/api/users/me/personal-token",
    Body: CreateApiKeyBodySchema,
    Response: ApiSuccessSchema(CreatedKeySchema)
  },

  /** GET /api/users/me/personal-token — list the user's active personal tokens (secret masked). */
  ListPersonalTokens: {
    method: "GET" as const,
    path: "/api/users/me/personal-token",
    Response: ApiSuccessSchema(z.array(z.custom<MaskedPersonalToken>()))
  },

  /** PATCH /api/users/me/personal-token/:keyId — update label/scopes/expiry (secret masked). */
  UpdatePersonalToken: {
    method: "PATCH" as const,
    path: "/api/users/me/personal-token/:keyId",
    PathVars: ApiKeyPathVarsSchema,
    Body: UpdateApiKeyBodySchema,
    Response: ApiSuccessSchema(z.custom<MaskedPersonalToken>())
  },

  /** DELETE /api/users/me/personal-token/:keyId — revoke a personal token. */
  RevokePersonalToken: {
    method: "DELETE" as const,
    path: "/api/users/me/personal-token/:keyId",
    PathVars: ApiKeyPathVarsSchema,
    Response: ApiSuccessSchema(ApiKeyOkSchema)
  },

  /** POST /api/users/checkout-session — create a Stripe checkout session; returns its id. */
  CreateCheckoutSession: {
    method: "POST" as const,
    path: "/api/users/checkout-session",
    Params: CheckoutSessionParamsSchema,
    Response: ApiSuccessSchema(CheckoutSessionSchema)
  },

  /** POST /api/users/topup — charge the org's default card to add credits. */
  Topup: {
    method: "POST" as const,
    path: "/api/users/topup",
    Params: TopupParamsSchema,
    Response: ApiSuccessSchema(TopupResultSchema)
  },

  /** POST /api/users/toggle-automatic-topup — enable/disable automatic balance recharge. */
  ToggleAutomaticTopup: {
    method: "POST" as const,
    path: "/api/users/toggle-automatic-topup",
    Body: ToggleAutomaticTopupBodySchema,
    Response: ApiSuccessSchema(ToggleTopupResultSchema)
  },

  /** POST /api/users/upgrade-to-pro — move the org's subscription to the Pro plan. */
  UpgradeToPro: {
    method: "POST" as const,
    path: "/api/users/upgrade-to-pro",
    Response: ApiSuccessSchema(SubscriptionMutationResultSchema)
  },

  /** POST /api/users/downgrade-to-hobby — move the org's subscription to the Hobby plan. */
  DowngradeToHobby: {
    method: "POST" as const,
    path: "/api/users/downgrade-to-hobby",
    Response: ApiSuccessSchema(SubscriptionMutationResultSchema)
  },

  /** POST /api/users/unsubscribe — cancel the org's active subscription. */
  Unsubscribe: {
    method: "POST" as const,
    path: "/api/users/unsubscribe",
    Response: ApiSuccessSchema(SubscriptionMutationResultSchema)
  },

  /** POST /api/users/unsubscribeTrial — cancel the org's trial subscription. */
  UnsubscribeTrial: {
    method: "POST" as const,
    path: "/api/users/unsubscribeTrial",
    Response: ApiSuccessSchema(SubscriptionMutationResultSchema)
  },

  /** GET /api/users/me/settings — current user's account + active-org settings. */
  GetSettings: {
    method: "GET" as const,
    path: "/api/users/me/settings",
    Response: ApiSuccessSchema(z.custom<UserSettingsResponse>())
  },

  /** POST /api/users/me/tracking — upsert the user's analytics tracking attributes. */
  TrackingUser: {
    method: "POST" as const,
    path: "/api/users/me/tracking",
    Body: TrackingSchema,
    Response: ApiSuccessSchema(z.null())
  }
} as const;
